import React, { useMemo } from "react";
import styled from "styled-components";

import AvatarStack, {
  AvatarStackItem,
} from "../avatar-stack/AvatarStack";
import { useGetDmThreadsQuery } from "../../app/services/dmThreads";
import type { DmThread, DmThreadStatus } from "../../app/services/dmThreads";
import { useGetAllProjectMembershipsQuery } from "../../app/accounts/memberships";
import type { ProjectMembership } from "../../app/accounts/memberships";

/**
 * 「引き受け済み」とみなすスレッドのステータス。
 * - in_progress: 進行中（着手済み）
 * - inspection_pending: 検収中（納品済み）
 * - completed: 完了（検収済み）
 *
 * `closed` / `rejected` / 見積中 / 提案中 は除外する。
 */
const ACCEPTED_STATUSES: ReadonlySet<DmThreadStatus> = new Set([
  "in_progress",
  "inspection_pending",
  "completed",
]);

const EmptyState = styled.div`
  width: 100%;
  padding: 12px;
  text-align: center;
  color: var(--soremo-light-gray);
`;

export interface OfferContractor {
  /** (user_id, role) のペアでユニークな識別子 */
  key: string;
  user_id: number;
  display_name: string;
  display_name_en: string;
  avatar_url?: string;
  role: string;
}

const buildContractorKey = (userId: number, role: string): string =>
  `${userId}::${role}`;

/**
 * (user_id, role) のペアでユニーク化する。
 * 同じユーザーが複数のロールで引き受けていれば、ロール毎に別エントリーとして表示する。
 * 同じユーザー × 同じロールの重複（同条件で複数オファーがある場合）は 1 件にまとめる。
 *
 * 表示名は DM スレッドが持つのが日本語名のみのため、memberships から
 * `display_name_jp` / `display_name_en` をマージして英語名も補完する。
 */
const dedupeContractors = (
  threads: DmThread[],
  memberships: ProjectMembership[],
): OfferContractor[] => {
  const memberById = new Map<number, ProjectMembership>();
  for (const m of memberships) {
    if (m.user_id) memberById.set(m.user_id, m);
  }

  const map = new Map<string, OfferContractor>();
  for (const t of threads) {
    // `is_transactional` はスレッドが「契約・支払いをプラットフォームで管理する」フラグ
    // （既定 `true`）。引き受け済み判定としてはこれが立っているか、進行中以降の
    // ステータスに到達しているかで合算する。
    const accepted = t.is_transactional && ACCEPTED_STATUSES.has(t.status);
    if (!accepted) continue;
    if (!t.contractor) continue;
    const id = t.contractor.id;
    if (!id) continue;
    const role = (t.assigned_roles || "").trim();
    const key = buildContractorKey(id, role);
    if (map.has(key)) continue;

    const member = memberById.get(id);
    const displayName =
      member?.display_name_jp || t.contractor.display_name || "";
    const displayNameEn =
      member?.display_name_en || t.contractor.display_name || "";

    map.set(key, {
      key,
      user_id: id,
      display_name: displayName,
      display_name_en: displayNameEn,
      avatar_url: t.contractor.avatar_url,
      role,
    });
  }
  return Array.from(map.values());
};

export interface OfferContractorAvatarsProps {
  projectId: string;
  selectedKey: string | null;
  onSelect: (contractor: OfferContractor) => void;
}

/**
 * クレジット編集の「クレジット記載者の候補一覧」（引き受け済みオファー受託者）を
 * 横並びのアバタースタックで表示する。
 *
 * 表示名・ロールは AvatarStack に詳細表示の責務を持たせないため、ツールチップに
 * 集約する。選択結果は呼び出し側の `EntryFormModal` のフォームフィールド
 * （表示名・ロール・linked_user_id）に反映される。
 */
const OfferContractorAvatars: React.FC<OfferContractorAvatarsProps> = ({
  projectId,
  selectedKey,
  onSelect,
}) => {
  const { data, isLoading } = useGetDmThreadsQuery(
    { project_id: projectId, type: "all", limit: 100 },
    { skip: !projectId },
  );
  const { data: membershipsData } = useGetAllProjectMembershipsQuery(
    { projectId },
    { skip: !projectId },
  );

  const contractors = useMemo(
    () =>
      dedupeContractors(
        data?.data?.threads || [],
        membershipsData?.data || [],
      ),
    [data, membershipsData],
  );

  const stackItems = useMemo<AvatarStackItem[]>(
    () =>
      contractors.map((c) => ({
        id: c.key,
        userId: c.user_id,
        avatar: c.avatar_url,
        displayName: c.role
          ? `${c.display_name}（${c.role}）`
          : c.display_name,
      })),
    [contractors],
  );

  if (isLoading) {
    return <EmptyState className="bodytext-13">読み込み中...</EmptyState>;
  }

  if (contractors.length === 0) {
    return (
      <EmptyState className="bodytext-13">
        引き受け済みのオファーはありません
      </EmptyState>
    );
  }

  const contractorByKey = new Map(contractors.map((c) => [c.key, c]));

  return (
    <AvatarStack
      items={stackItems}
      size={40}
      selectedId={selectedKey}
      onSelect={(id) => {
        const c = contractorByKey.get(String(id));
        if (c) onSelect(c);
      }}
      ariaLabel="引き受け済みオファー受託者"
    />
  );
};

export default OfferContractorAvatars;
